import React, { useEffect, useState } from 'react';
import axios from '../lib/axios';
import { Plus, Baby as BabyIcon, Search, Calendar, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const Children = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newChild, setNewChild] = useState({
    name: '',
    dob: '',
    classroom_id: '',
    allergies: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [childrenRes, classroomsRes] = await Promise.all([
        axios.get('/children'),
        axios.get('/classrooms'),
      ]);
      
      setChildren(childrenRes.data);
      setClassrooms(classroomsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load children');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/children', newChild);
      toast.success('Child added successfully!');
      setIsAddDialogOpen(false);
      setNewChild({ name: '', dob: '', classroom_id: '', allergies: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Error adding child:', error);
      toast.error(error.response?.data?.detail || 'Failed to add child');
    }
  };

  const filteredChildren = children.filter(child =>
    child.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getClassroomName = (classroomId) => {
    const classroom = classrooms.find(c => c.id === classroomId);
    return classroom?.name || 'Unknown';
  };

  // Group children by classroom and sort alphabetically
  const childrenByClassroom = {};
  filteredChildren.forEach(child => {
    if (!childrenByClassroom[child.classroom_id]) {
      childrenByClassroom[child.classroom_id] = [];
    }
    childrenByClassroom[child.classroom_id].push(child);
  });

  // Sort children within each classroom alphabetically
  Object.keys(childrenByClassroom).forEach(classroomId => {
    childrenByClassroom[classroomId].sort((a, b) => a.name.localeCompare(b.name));
  });

  // Sort classrooms by name
  const sortedClassrooms = classrooms
    .filter(c => childrenByClassroom[c.id] && childrenByClassroom[c.id].length > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
    months -= birthDate.getMonth();
    months += today.getMonth();
    
    if (months < 12) {
      return `${months}mo`;
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      return remainingMonths > 0 ? `${years}y ${remainingMonths}mo` : `${years}y`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="children-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Children
          </h1>
          <p className="text-slate-600 mt-1">Manage child records and profiles</p>
        </div>
        {user?.role !== 'parent' && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-child-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Child
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Child</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddChild} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={newChild.name}
                    onChange={(e) => setNewChild({ ...newChild, name: e.target.value })}
                    placeholder="e.g., Emma Johnson"
                    required
                    className="mt-1.5"
                    data-testid="child-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                    id="dob"
                    type="date"
                    value={newChild.dob}
                    onChange={(e) => setNewChild({ ...newChild, dob: e.target.value })}
                    required
                    className="mt-1.5"
                    data-testid="child-dob-input"
                  />
                </div>
                <div>
                  <Label htmlFor="classroom">Classroom</Label>
                  <Select
                    value={newChild.classroom_id}
                    onValueChange={(value) => setNewChild({ ...newChild, classroom_id: value })}
                    required
                  >
                    <SelectTrigger className="mt-1.5" data-testid="child-classroom-select">
                      <SelectValue placeholder="Select classroom" />
                    </SelectTrigger>
                    <SelectContent>
                      {classrooms.map((classroom) => (
                        <SelectItem key={classroom.id} value={classroom.id}>
                          {classroom.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="allergies">Allergies (Optional)</Label>
                  <Input
                    id="allergies"
                    value={newChild.allergies}
                    onChange={(e) => setNewChild({ ...newChild, allergies: e.target.value })}
                    placeholder="e.g., Peanuts, Dairy"
                    className="mt-1.5"
                    data-testid="child-allergies-input"
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes (Optional)</Label>
                  <Input
                    id="notes"
                    value={newChild.notes}
                    onChange={(e) => setNewChild({ ...newChild, notes: e.target.value })}
                    placeholder="Additional information"
                    className="mt-1.5"
                    data-testid="child-notes-input"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" className="flex-1" data-testid="submit-child-button">
                    Add Child
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsAddDialogOpen(false)}
                    data-testid="cancel-child-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="search"
          placeholder="Search children..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="search-children-input"
        />
      </div>

      {/* Children Grid - Grouped by Classroom */}
      <div className="space-y-8">
        {sortedClassrooms.map((classroom) => (
          <div key={classroom.id}>
            {/* Classroom Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                <Users className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {classroom.name}
                </h2>
                <span className="text-sm text-slate-600 ml-2">
                  ({childrenByClassroom[classroom.id].length} {childrenByClassroom[classroom.id].length === 1 ? 'child' : 'children'})
                </span>
              </div>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>

            {/* Children Grid for this Classroom */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {childrenByClassroom[classroom.id].map((child) => (
                <Link
                  key={child.id}
                  to={`/children/${child.id}`}
                  className="bg-white rounded-xl border border-slate-100 p-6 hover:border-primary/20 hover:shadow-md transition-all group"
                  data-testid={`child-card-${child.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center group-hover:from-blue-200 group-hover:to-purple-200 transition-colors">
                      <BabyIcon className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                      {calculateAge(child.dob)}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {child.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-2">DOB: {new Date(child.dob).toLocaleDateString()}</p>
                  {child.allergies && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs text-rose-600 font-medium">⚠️ Allergies: {child.allergies}</p>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>

      {sortedClassrooms.length === 0 && (
        <div className="text-center py-12">
          <BabyIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No children found</h3>
          <p className="text-slate-600 mb-4">
            {searchTerm ? 'Try adjusting your search' : 'Get started by adding your first child'}
          </p>
          {user?.role !== 'parent' && !searchTerm && (
            <Button onClick={() => setIsAddDialogOpen(true)} data-testid="empty-state-add-button">
              <Plus className="w-4 h-4 mr-2" />
              Add First Child
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Children;
